import { Alert, Divider, Group, Stack, Text } from '@mantine/core';
import { IconAlertCircle, IconPencil } from '@tabler/icons-react';
import { Button } from 'src/design-system/Button';
import { TermsAndPrivacy } from 'src/auth/components/TermsAndPrivacy';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { WizardShell } from '../components/WizardShell';

export type ReviewEditableField = 'firstName' | 'lastName' | 'companyName' | 'frn';

type ReviewStepProps = {
  values: {
    firstName: string;
    lastName: string;
    companyName: string;
    frn: string;
    email: string;
  };
  direction: 1 | -1;
  currentStepIndex: number;
  totalSteps: number;
  onBack: () => void;
  onSubmit: () => void;
  onEditField: (field: ReviewEditableField) => void;
  isSubmitting: boolean;
  topLevelError: string | null;
  ambiguousTimeout: boolean;
};

const REVIEW_ROWS: { field: ReviewEditableField; label: string }[] = [
  { field: 'firstName', label: 'First name' },
  { field: 'lastName', label: 'Last name' },
  { field: 'companyName', label: 'Company name' },
  { field: 'frn', label: 'FCA firm reference number' },
];

export function ReviewStep({
  values,
  onBack,
  onSubmit,
  onEditField,
  isSubmitting,
  topLevelError,
  ambiguousTimeout,
}: ReviewStepProps) {
  return (
    <WizardShell
      title="Check your details"
      onBack={onBack}
      onAction={onSubmit}
      actionLabel={ambiguousTimeout ? 'Try again' : 'Create my account'}
      actionLoading={isSubmitting}
    >
      <Stack gap="lg">
        {topLevelError && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            variant="light"
            title="We could not complete your submission"
          >
            {topLevelError}
          </Alert>
        )}

        <Stack gap="xs">
          <ReviewRow label="Email address" value={values.email} />
          <Divider />
          {REVIEW_ROWS.map(({ field, label }) => (
            <ReviewRow
              key={field}
              label={label}
              value={values[field]}
              onEdit={() => onEditField(field)}
            />
          ))}
        </Stack>

        <TermsAndPrivacy />

        {ambiguousTimeout && (
          <Text size="xs" c="gray.6" ta="center">
            Please wait a few minutes before trying again, in case your previous submission is still
            being processed.
          </Text>
        )}
      </Stack>
    </WizardShell>
  );
}

function ReviewRow({
  label,
  value,
  onEdit,
}: {
  label: string;
  value: string;
  onEdit?: () => void;
}) {
  return (
    <Group justify="space-between" wrap="nowrap" gap="sm">
      <Stack gap={0} style={{ minWidth: 0 }}>
        <Text size="xs" c="gray.6">
          {label}
        </Text>
        <Text fw={FONT_WEIGHTS.semibold} c="gray.10" truncate>
          {value}
        </Text>
      </Stack>
      {onEdit && (
        <Button
          variant="text"
          leftSection={<IconPencil size={14} />}
          onClick={onEdit}
          aria-label={`Edit ${label.toLowerCase().replace('fca', 'FCA')}`}
        >
          Edit
        </Button>
      )}
    </Group>
  );
}
